// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    disasterRecoveryHistory,
    IDisasterRecoveryDeleteOriginalDataRequest,
    IDisasterRecoveryDownloadOriginalDataRequest,
    IDisasterRecoveryEntitiesRequest,
    IDisasterRecoveryEntityResponse,
    IDisasterRecoveryHistoryRequest,
    IDisasterRecoveryResponse,
} from 'common/api/resources/DisasterRecovery';
import { Dispatch } from 'redux';
import {
    setIsLoading,
    unsetIsLoading,
} from 'common/modules/app/loadingFlags/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { HTTP_CODES } from 'common/api/constants';
import { createCustomAction } from 'typesafe-actions';
import * as types from 'common/modules/disasterRecovery/constants/types';
import { IPaginateApiResponse } from 'common/api/resources/Response';
import { bakeForegroundToast } from 'common/modules/app/toaster/actions';
import { INTENT_TYPE } from 'common/constants';
import { ICommonState } from 'common/store';
import { loadOnScroll } from 'common/actions/actionsWrapper';

export const setDisasterRecoveryHistory = createCustomAction(
    types.SET_DISASTER_RECOVERY_HISTORY,
    (data: IPaginateApiResponse<IDisasterRecoveryResponse[]>) => ({ payload: data })
);

export const setDisasterRecoveryEntitiesForProcess = createCustomAction(
    types.SET_DISASTER_RECOVERY_PROCESS_ENTITIES,
    (data: { data: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>; processId: number}) => ({ payload: data })
);

export const setDisasterRecoveryEntities = createCustomAction(
    types.SET_DISASTER_RECOVERY_ENTITIES,
    (data: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>) => ({ payload: data })
);

export const setOriginalDataManuallyDeletedEntityIds = createCustomAction(
    types.DELETE_DISASTER_RECOVERY_ORIGINAL_DATA,
    (data: number[]) => ({ payload: data })
);

export const setDisasterRecoveryEntitiesDeletingOriginalData = createCustomAction(
    types.SET_DISASTER_RECOVERY_ENTITIES_DELETING_ORIGINAL_DATA,
    (data: IDisasterRecoveryDeleteOriginalDataRequest) => ({ payload: data })
);

export const unsetDisasterRecoveryEntitiesDeletingOriginalData = createCustomAction(
    types.UNSET_DISASTER_RECOVERY_ENTITIES_DELETING_ORIGINAL_DATA,
    (data: IDisasterRecoveryDeleteOriginalDataRequest) => ({ payload: data })
);

export const setDisasterRecoveryProcessLoading = createCustomAction(
    types.SET_DISASTER_RECOVERY_PROCESS_LOADING,
    (id: number) => ({ payload: id })
);

export const unsetDisasterRecoveryProcessLoading = createCustomAction(
    types.UNSET_DISASTER_RECOVERY_PROCESS_LOADING,
    (id: number) => ({ payload: id })
);

export const appendDisasterRecoveryEntities = createCustomAction(
    types.APPEND_DISASTER_RECOVERY_ENTITIES,
    (data: { data: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>; processId: number }) => ({ payload: data })
);

export const getDisasterRecoveryHistory = (request?: IDisasterRecoveryHistoryRequest) => async(dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_HISTORY));

    try {
        const result = await disasterRecoveryHistory.list(request);
        if (result.status === HTTP_CODES.OK) {
            dispatch(setDisasterRecoveryHistory(result.data));
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_HISTORY));
    }
};

export const getDisasterRecoveryEntities = (request: IDisasterRecoveryEntitiesRequest, perPage?: number) => async(dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES));

    try {
        const result = await disasterRecoveryHistory.entities(request, perPage);
        if (result.status === HTTP_CODES.OK) {
            dispatch(setDisasterRecoveryEntities(result.data));
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES));
    }
};

export const getDisasterRecoveryEntitiesForProcess = (id: number, perPage: number = 50) => async(dispatch: Dispatch) => {
    dispatch(setDisasterRecoveryProcessLoading(id));

    try {
        const result = await disasterRecoveryHistory.entities({
            filters: {
                disaster_recovery_id: [id],
                disaster_recovery_entity_id: [],
            },
            per_page: perPage,
        });
        if (result.status === HTTP_CODES.OK) {
            dispatch(setDisasterRecoveryEntitiesForProcess({
                data: result.data,
                processId: id,
            }));
        }

        return result;
    } finally {
        dispatch(unsetDisasterRecoveryProcessLoading(id));
    }
};

export const loadMoreDisasterRecoveryEntitiesOnScroll = (process: IDisasterRecoveryResponse) => async (dispatch: Dispatch, getState: () => ICommonState) => {
    const state = getState();
    const isLoading = state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES_INFINITY_SCROLL);
    const nextPage = process.entities?.links.next;
    if (!nextPage) {
        return;
    }

    return await loadOnScroll({
        nextPage,
        isLoading,
        dispatch,
        action: (param: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>) => appendDisasterRecoveryEntities({
            data: param, processId: process.id,
        }),
        loadingFlag: LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES_INFINITY_SCROLL,
    });
};

export const deleteOriginalData = (request: IDisasterRecoveryDeleteOriginalDataRequest) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_ORIGINAL_DATA_DELETING));
    dispatch(setDisasterRecoveryEntitiesDeletingOriginalData(request));

    try {
        const result = await disasterRecoveryHistory.deleteOriginalData(request);
        if (result.status === HTTP_CODES.OK) {
            if (request.is_deleted_manually) {
                dispatch(setOriginalDataManuallyDeletedEntityIds(result.data));
                bakeForegroundToast(INTENT_TYPE.SUCCESS, 'disasterRecovery.history.originalDataDeletedManually')(dispatch);
            } else {
                bakeForegroundToast(INTENT_TYPE.SUCCESS, 'disasterRecovery.history.originalDataDeleteTask')(dispatch);
            }
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_ORIGINAL_DATA_DELETING));
        dispatch(unsetDisasterRecoveryEntitiesDeletingOriginalData(request));
    }
};

export const downloadOriginalData = (request: IDisasterRecoveryDownloadOriginalDataRequest) => async (dispatch: Dispatch) =>  {
    dispatch(setIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_DOWNLOADING_ORIGINAL_DATA));
    try {
        await disasterRecoveryHistory.downloadOriginalData(request);
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.DISASTER_RECOVERY_DOWNLOADING_ORIGINAL_DATA));
    }
};
